'use client'

import Lenis from '@studio-freight/lenis'
import {shallow} from 'zustand/shallow'
import {createWithEqualityFn} from 'zustand/traditional'

const config = {
	lerp: 0.12,
	orientation: 'vertical', // vertical, horizontal
	gestureOrientation: 'vertical', // vertical, horizontal, both
	wheelMultiplier: 1,
	normalizeWheel: false,
	smoothTouch: false,
}

/** `ScrollToTargetType`: goal to reach
 * number: value to scroll in pixels
 * string: CSS selector or keyword (top, left, start, bottom, right, end)
 * HTMLElement: DOM element
 */
type ScrollToTargetType = number | HTMLElement | 'top' | 'left' | 'start' | 'bottom' | 'right' | 'end'

type ScrollToOptionProps = {
	/** `offset`: equivalent to scroll-padding-top */
	offset?: number
	/** `lerp`: animation lerp intensity */
	lerp?: number
	/** `duration`: animation duration (in seconds) */
	duration?: number
	/** `easing`: animation easing */
	easing?: (t:number) => number
	/** `immediate`: ignore duration, easing and lerp */
	immediate?: boolean
	/** `lock`: whether or not to prevent the user from scrolling until the target is reached */
	lock?: boolean
	/** `force`: reach target even if instance is stopped */
	force?: boolean
	/** `onComplete`: called when the target is reached */
	onComplete?: () => void
}

interface ScrollState {
	isLocked: boolean,
	savedScrollY: number,
	setIsLocked: (value: boolean) => void,
	lenis: Lenis,
	scrollTo: (value: ScrollToTargetType, options?: ScrollToOptionProps) => void,
}

export const useScrollStore = createWithEqualityFn<ScrollState>((set, get) => ({
	isLocked: false,
	savedScrollY: 0,
	setIsLocked: value => {
		const body = document.body
		const lenis = get().lenis
		const isLocked = get().isLocked

		if (value && !isLocked) {
			const scrollY = lenis.animatedScroll
			// lenis.stop()
			lenis.destroy()
			body.style.position = 'fixed'
			body.style.top = `-${scrollY}px`
			body.style.left = '0'
			body.style.right = `var(--sbw, 0px)`

			set(() => ({
				isLocked: value,
				savedScrollY: scrollY,
			}))
		} else if (!value && isLocked) {
			body.style.position = ''
			body.style.top = ''
			set({lenis: new Lenis(config)})
			// lenis.start()
			get().lenis.scrollTo(get().savedScrollY, {immediate: true, force: true})
			set(() => ({
				isLocked: value,
				savedScrollY: 0,
			}))
		}
	},
	lenis: typeof (window) !== 'undefined' && new Lenis(config),
	scrollTo: (value, options) => get().lenis.scrollTo(value, options),
}), shallow)
